import { create } from 'zustand';
import {
  type CombinedHealthStatus,
  HealthLevel,
  MonitoredService,
  type PlatformStatus,
  type ProjectHealth,
  type ServiceHealthStatus,
  ServiceStatus,
} from '@/models/health-status.model';

/**
 * @fileoverview Health store (Zustand)
 * @description
 * React/Zustand port of the Angular `HealthService` (local mode). In
 * local-storage mode there is no remote backend, so this reports a healthy
 * status synchronously. Signal names, method names, and the health-check
 * logic are reproduced 1:1.
 */

interface HealthState {
  /** Current project health status */
  projectHealth: ProjectHealth | null;
  /** Current platform status */
  platformStatus: PlatformStatus | null;
  /** Combined health assessment */
  combinedStatus: CombinedHealthStatus | null;
  /** Whether currently checking health */
  isChecking: boolean;
  /** Last check time */
  lastCheckTime: number | null;

  checkAllHealth: (forceRefresh?: boolean) => Promise<CombinedHealthStatus>;
  clearCache: () => void;
}

/** Build the operational-service list (was private healthyServices). */
function healthyServices(): ServiceHealthStatus[] {
  const services = [MonitoredService.AUTH, MonitoredService.DATABASE, MonitoredService.STORAGE];
  return services.map((service) => ({
    service,
    name: service.toString(),
    status: ServiceStatus.OPERATIONAL,
    message: 'Service is operational',
    lastUpdated: Date.now(),
    uptimePercentage: 100,
  }));
}

/** Build the all-healthy combined status (was private createHealthyStatus). */
function createHealthyStatus(): CombinedHealthStatus {
  const services = healthyServices();
  const project: ProjectHealth = {
    level: HealthLevel.HEALTHY,
    services,
    isAccessible: true,
    projectRef: 'local',
    checkedAt: Date.now(),
  };
  const platform: PlatformStatus = {
    level: HealthLevel.HEALTHY,
    services,
    activeIncidents: [],
    recentIncidents: [],
    fetchedAt: Date.now(),
    scheduledMaintenance: [],
  };
  return {
    project,
    platform,
    overall: HealthLevel.HEALTHY,
    issueScope: 'none',
    recommendation: 'All systems operational',
    assessedAt: Date.now(),
  };
}

export const useHealthStore = create<HealthState>((set) => ({
  projectHealth: null,
  platformStatus: null,
  combinedStatus: null,
  isChecking: false,
  lastCheckTime: null,

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  checkAllHealth: async (_forceRefresh = false) => {
    const combined = createHealthyStatus();
    set({
      projectHealth: combined.project,
      platformStatus: combined.platform,
      combinedStatus: combined,
      lastCheckTime: Date.now(),
    });
    return combined;
  },

  clearCache: () => {
    set({
      projectHealth: null,
      platformStatus: null,
      combinedStatus: null,
      lastCheckTime: null,
    });
  },
}));
